import * as React from 'react';

import { Box, Tooltip } from '@mui/material';

/**
 * A compact fixed-order ggvar strip: one cell per attribute, colour encoding
 * deviation from the anchor mean.
 *
 * The point is vertical scanning. Because every row uses the same attribute
 * order and the same scale, a column of strips makes an off-profile candidate
 * visible without reading any numbers.
 */

type Props = {
  /** attribute -> intensity for this product */
  values: Record<string, number>;
  /** attribute -> mean intensity across the anchors (the reference) */
  reference?: Record<string, number>;
  /** shared attribute order, so cells line up across rows */
  attributes: string[];
  /** largest absolute deviation in the table, for a shared colour scale */
  maxAbsDelta?: number;
  cellWidth?: number;
  height?: number;
};

const POS = '199, 84, 46'; // above reference
const NEG = '58, 124, 165'; // below reference

export const deltaColor = (delta: number, maxAbs: number): string => {
  if (!Number.isFinite(delta) || maxAbs <= 0) return 'rgba(0,0,0,0.06)';
  // sqrt so small, common deviations stay visible instead of washing out
  const mag = Math.min(1, Math.sqrt(Math.abs(delta) / maxAbs));
  return `rgba(${delta >= 0 ? POS : NEG}, ${(0.12 + 0.78 * mag).toFixed(3)})`;
};

const GgvarStrip: React.FC<Props> = ({
  values,
  reference,
  attributes,
  maxAbsDelta = 1,
  cellWidth = 9,
  height = 20,
}) => (
  <Box sx={{ display: 'flex', gap: '1px' }}>
    {attributes.map((attr) => {
      const v = values?.[attr];
      const ref = reference?.[attr];
      const has = Number.isFinite(v);
      const delta = has && Number.isFinite(ref) ? v - (ref as number) : NaN;

      const title = !has
        ? `${attr}: no data`
        : Number.isFinite(delta)
          ? `${attr}: ${v.toFixed(2)} (${delta >= 0 ? '+' : ''}${delta.toFixed(2)} vs anchors)`
          : `${attr}: ${v.toFixed(2)}`;

      return (
        <Tooltip key={attr} title={title} arrow enterDelay={300}>
          <Box
            sx={{
              width: cellWidth,
              height,
              borderRadius: '2px',
              // Missing data must not read as "no deviation" — hatch it.
              backgroundColor: has
                ? deltaColor(delta, maxAbsDelta)
                : 'transparent',
              backgroundImage: has
                ? 'none'
                : 'repeating-linear-gradient(45deg, rgba(0,0,0,0.16) 0 2px, transparent 2px 4px)',
              border: '1px solid rgba(0,0,0,0.08)',
            }}
          />
        </Tooltip>
      );
    })}
  </Box>
);

export default GgvarStrip;
